interface Config {
    title: string
    apiBaseUrl: string
    env: string
    version: string
    googleClientId: string
    isDev: boolean
    apExamMockPaperPickerEnabled: boolean
    expertApiToken: string
    expertApiBaseUrl: string
    googleCseCx: string,
    parentPayTokenKey: string
    siteUrl: string
    firestoreDatabaseId: string
    firestoreCollectionWebSparksUser: string,
    firestoreCollectionWebSparksRequest: string
    internalSecret: string
}

export const PROD_SITE_URL = 'https://mysovi.ai'
export const UAT_SITE_URL = 'https://uat.mysovi.ai'

function normalizeSiteUrl(url: string): string {
    return url.replace(/\/+$/, '')
}

function runtimeString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : ''
}

function getRuntimeConfig() {
    return typeof window !== 'undefined' ? window.__SOVI_RUNTIME_CONFIG__ : undefined
}

function resolveHost(): string {
    return typeof window !== 'undefined' ? window.location.hostname : ''
}

function resolveSiteUrl(): string {
    const explicitSiteUrl = import.meta.env.VITE_SITE_URL
    if (explicitSiteUrl) return normalizeSiteUrl(explicitSiteUrl)

    if (typeof window !== 'undefined') {
        const host = resolveHost()
        if (host === 'uat.mysovi.ai' || host === 'uat.sovi-ai-web-v2.pages.dev') return UAT_SITE_URL
        if (host === 'mysovi.ai' || host === 'www.mysovi.ai') return PROD_SITE_URL
        if (host === 'localhost' || host === '127.0.0.1') return window.location.origin
    }

    const appEnv = String(import.meta.env.VITE_APP_ENV || '').toLowerCase()
    if (appEnv.includes('uat') || appEnv.includes('staging')) return UAT_SITE_URL

    return PROD_SITE_URL
}

function resolveDefaultFirestoreDatabaseId(): string {
    const host = resolveHost()
    if (host === 'uat.mysovi.ai' || host === 'uat.sovi-ai-web-v2.pages.dev') return 'solvex-ai-dev'
    if (host === 'mysovi.ai' || host === 'www.mysovi.ai') return 'solvex-ai-prod'

    const appEnv = String(import.meta.env.VITE_APP_ENV || '').toLowerCase()
    if (appEnv.includes('production') || appEnv === 'prod') return 'solvex-ai-prod'
    return 'solvex-ai-dev'
}

function buildTimeFirestoreDatabaseId(): string {
    const value = runtimeString(import.meta.env.VITE_FIRESTORE_DATABASE_ID)
    const host = resolveHost()
    if ((host === 'uat.mysovi.ai' || host === 'uat.sovi-ai-web-v2.pages.dev') && value === 'solvex-ai-prod') return ''
    if ((host === 'mysovi.ai' || host === 'www.mysovi.ai') && value === 'solvex-ai-dev') return ''
    return value
}

const runtimeConfig = getRuntimeConfig()

const config: Config = {
    title: import.meta.env.VITE_APP_TITLE,
    apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL,
    env: import.meta.env.VITE_APP_ENV,
    version: import.meta.env.VITE_APP_VERSION,
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    isDev: import.meta.env.VITE_APP_ENV == 'development',
    parentPayTokenKey: import.meta.env.VITE_PARENT_PAY_TOKEN_KEY||'51128417771151672651777115173785',
    apExamMockPaperPickerEnabled: true,
    expertApiToken: import.meta.env.VITE_EXPERT_API_TOKEN || 'sk-s-uWqhFqZ-W2nPg33tCQDXU6gkHWNrFQK3_TEqbngls',
    expertApiBaseUrl: import.meta.env.VITE_EXPERT_API_BASE_URL || resolveSiteUrl(),
    googleCseCx: import.meta.env.VITE_GOOGLE_CSE_CX || 'c753552a337d8407a',
    siteUrl: resolveSiteUrl(),
    // Cloudflare Pages 的 Vite 环境变量只在构建期替换；运行时变量由 Pages Function
    // 注入到 window.__SOVI_RUNTIME_CONFIG__。最后按域名兜底，避免 UAT 构建拿不到变量时
    // 退回 Firestore 默认库。
    firestoreDatabaseId:
        runtimeString(runtimeConfig?.firestoreDatabaseId) ||
        runtimeString(typeof window !== 'undefined' ? window.SOVI_FIRESTORE_DATABASE_ID : undefined) ||
        buildTimeFirestoreDatabaseId() ||
        resolveDefaultFirestoreDatabaseId(),
    firestoreCollectionWebSparksUser:
        runtimeString(runtimeConfig?.firestoreCollectionWebSparksUser) ||
        runtimeString(typeof window !== 'undefined' ? window.SOVI_FIRESTORE_SPARKS_COLLECTION : undefined) ||
        import.meta.env.VITE_FIRESTORE_COLLECTION_WEB_SPARKS_USER ||
        'web_sparks_user',
    firestoreCollectionWebSparksRequest:
        runtimeString(runtimeConfig?.firestoreCollectionWebSparksRequest) ||
        import.meta.env.VITE_FIRESTORE_COLLECTION_WEB_SPARKS_REQUEST ||
        'web_sparks_request',
    // 调用主站 Java 核心积分服务 limit-check 的 S2S 身份密钥（X-Internal-Secret）
    internalSecret: import.meta.env.VITE_INTERNAL_SECRET || '29322c7f-5f3e-11f1-8988-000c29b0fcf7'
}

export default config
